import React, { useCallback, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { Tooltip } from 'antd';
import Icon from '@ant-design/icons';
import PauseCircleSvg from '@/assets/svg/PauseCircleIconSvg';
import PlayCircleSvg from '@/assets/svg/PlayCircleIconSvg';
import RestartCircleIconSvg from '@/assets/svg/RestartCircleIconSvg';
import TimelineDots from '../InteractiveSimulation/TimeLineDots.tsx';
import './TimePlayer.scss';

dayjs.extend(isSameOrAfter);

const TimePlayer = ({
	startDate,
	endDate,
	step,
	title = '',
	stepsBySecond = 1,
	events = [],
	onStateChange,
	autoStart = false,
	intervals = 10,
	subdivisions = 5,
	resetTrigger,
}) => {
	const [isPlaying, setIsPlaying] = useState(autoStart);
	const [currentTime, setCurrentTime] = useState(startDate);
	const [timelineMarkerPosition, setTimelineMarkerPosition] = useState(0);
	const intervalRef = useRef(null);

	// Calculate current progress percentage
	const calculateProgress = useCallback(() => {
		if (!currentTime || !startDate || !endDate) return 0;

		const totalDuration = dayjs(endDate).diff(dayjs(startDate), 'millisecond');
		const currentDuration = dayjs(currentTime).diff(dayjs(startDate), 'millisecond');

		let progress = (currentDuration / totalDuration) * 100;
		return Math.min(Math.max(progress, 0), 100);
	}, [currentTime, startDate, endDate]);

	// Update timeline marker position
	const updateTimelinePosition = useCallback(() => {
		const progress = calculateProgress();
		setTimelineMarkerPosition(progress);
	}, [calculateProgress]);

	// Calculate event position on timeline
	const calculateEventPosition = useCallback(
		(eventTime) => {
			if (!startDate || !endDate) return 0;

			const totalDuration = dayjs(endDate).diff(dayjs(startDate), 'millisecond');
			const eventDuration = dayjs(eventTime).diff(dayjs(startDate), 'millisecond');

			return (eventDuration / totalDuration) * 100;
		},
		[startDate, endDate],
	);

	// Start timer
	const startTimer = useCallback(() => {
		if (intervalRef.current) return;

		const intervalMs = 1000 / stepsBySecond; // Calculate interval based on stepsBySecond

		intervalRef.current = setInterval(() => {
			setCurrentTime((prevTime) => {
				const nextTime = dayjs(prevTime).add(step, 'minute');

				// Check if we've reached the end
				if (nextTime.isAfter(dayjs(endDate))) {
					// Stop at the end and pause
					setIsPlaying(false);
					return endDate;
				}

				return nextTime.toISOString();
			});
		}, intervalMs);
	}, [step, endDate, stepsBySecond]);

	// Stop timer
	const stopTimer = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
	}, []);

	// Handle play/pause
	const handlePlayPause = () => {
		setIsPlaying((prev) => !prev);
	};

	// Handle timeline click to seek
	const handleTimelineClick = (event) => {
		const timeline = event.currentTarget;
		const rect = timeline.getBoundingClientRect();
		const clickX = event.clientX - rect.left;
		const percentage = (clickX / rect.width) * 100;

		const totalDuration = dayjs(endDate).diff(dayjs(startDate), 'millisecond');
		const newTime = dayjs(startDate).add((percentage / 100) * totalDuration, 'millisecond');

		setCurrentTime(newTime.toISOString());
	};

	// Reset to start
	const resetToStart = useCallback(() => {
		setCurrentTime(startDate);
		setIsPlaying(false);
	}, [startDate]);

	// Notify parent of state changes
	useEffect(() => {
		if (onStateChange) {
			const progress = calculateProgress();
			onStateChange({
				isPlaying,
				currentTime,
				progress,
				isAtEnd: dayjs(currentTime).isSameOrAfter(dayjs(endDate)),
			});
		}
	}, [isPlaying, currentTime, calculateProgress, onStateChange, endDate]);

	// Handle timer start/stop based on playing state
	useEffect(() => {
		if (isPlaying) {
			startTimer();
		} else {
			stopTimer();
		}

		return () => stopTimer();
	}, [isPlaying, startTimer, stopTimer]);

	// Update timeline position when current time changes
	useEffect(() => {
		updateTimelinePosition();
	}, [updateTimelinePosition]);

	// Handle external reset trigger
	useEffect(() => {
		if (resetTrigger) {
			resetToStart();
		}
	}, [resetTrigger, resetToStart]);

	// Cleanup on unmount
	useEffect(() => {
		return () => stopTimer();
	}, [stopTimer]);

	return (
		<div className="time-player-container">
			<div className="time-player-header">
				<div className="title-section">
					{title && <div className="time-player-title">{title}</div>}
				</div>

				<div className="controls-section">
					{dayjs(currentTime).isSameOrAfter(dayjs(endDate)) ? (
						<Tooltip title="Reset to start">
							<Icon
								component={RestartCircleIconSvg}
								className="control-button"
								style={{
									width: '32px',
									height: '32px',
									fontSize: '32px',
									color: '#22a2ca',
								}}
								onClick={resetToStart}
							/>
						</Tooltip>
					) : (
						<Tooltip title={isPlaying ? 'Pause' : 'Play'}>
							<Icon
								component={isPlaying ? PauseCircleSvg : PlayCircleSvg}
								className="control-button"
								style={{
									width: '32px',
									height: '32px',
									fontSize: '32px',
									color: '#22a2ca',
								}}
								onClick={handlePlayPause}
							/>
						</Tooltip>
					)}
				</div>

				<div className="time-info">
					Dates Selected: {dayjs(startDate).format('YYYY-MM-DD hh:mm:ss A')}{' '}
					{dayjs(endDate).format('YYYY-MM-DD hh:mm:ss A')}
				</div>
			</div>

			<div className="timeline">
				<div className="timeline-bar" onClick={handleTimelineClick} style={{ cursor: 'pointer' }}>
					<TimelineDots
						startDate={startDate}
						endDate={endDate}
						intervals={intervals}
						subdivisions={subdivisions}
					/>

					<div
						className="current-time-marker"
						style={{
							left: `${timelineMarkerPosition}%`,
						}}
					>
						<div className="time-marker-header">
							<span>{dayjs(currentTime).format('YYYY-MM-DD')}</span>
							<span>{dayjs(currentTime).format('HH:mm:ss')}</span>
						</div>
					</div>

					{events.map((event, index) => {
						const position = calculateEventPosition(event.time);
						return (
							<div
								key={`event-${index}`}
								className="event-time-marker"
								style={{
									left: `${position}%`,
								}}
							>
								<Tooltip
									title={
										<div className="flex flex-col">
											<div className="flex text-xs">
												<span>{dayjs(event.time).format('YYYY-MM-DD HH:mm:ss')}</span>
											</div>
											{event.events &&
												event.events.map((ev, keyev) => (
													<div className="flex text-xs" key={`ttev-${keyev}`}>
														<span>
															{ev.infraName} to <b>{ev.mod_value}</b>
														</span>
													</div>
												))}
											{event.label && (
												<div className="flex text-xs">
													<span>{event.label}</span>
												</div>
											)}
										</div>
									}
								>
									<div className="time-marker-header">
										<span>{dayjs(event.time).format('YYYY-MM-DD')}</span>
										<span>{dayjs(event.time).format('HH:mm:ss')}</span>
									</div>
								</Tooltip>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};

TimePlayer.propTypes = {
	startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
	endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
	step: PropTypes.number.isRequired,
	title: PropTypes.string,
	stepsBySecond: PropTypes.number,
	events: PropTypes.arrayOf(
		PropTypes.shape({
			time: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
			label: PropTypes.string,
			events: PropTypes.array,
		}),
	),
	onStateChange: PropTypes.func,
	autoStart: PropTypes.bool,
	intervals: PropTypes.number,
	subdivisions: PropTypes.number,
	resetTrigger: PropTypes.any,
};

export default TimePlayer;
